import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Loader2, ArrowRight, RefreshCw } from 'lucide-react';
import { Link, useSearchParams, Navigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const { isAuthenticated, refreshWallet, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [result, setResult] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10;

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && isAuthenticated) {
      checkPaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isAuthenticated]);

  const checkPaymentStatus = async () => {
    if (pollCount >= maxPolls) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.status === 'completed' && data.payment_status === 'paid') {
        setStatus('success');
        setResult(data);
        refreshWallet(); // Refresh wallet balance
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Keep polling
        setPollCount(prev => prev + 1);
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (error.response?.status === 404) {
        setStatus('not_found');
      } else {
        setPollCount(prev => prev + 1);
        if (pollCount < maxPolls) {
          setTimeout(checkPaymentStatus, 2000);
        } else {
          setStatus('error');
        }
      }
    }
  };

  // Redirect if not authenticated
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // No session ID
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-white/10 border-white/20 backdrop-blur-sm max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Invalid Request</h2>
            <p className="text-blue-200 mb-6">No payment session found.</p>
            <Link to="/buy-credits">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Back to Buy Credits
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm max-w-md w-full" data-testid="payment-result-card">
        <CardContent className="p-8 text-center">
          {/* Checking Status */}
          {status === 'checking' && (
            <>
              <Loader2 className="mx-auto h-16 w-16 text-blue-400 mb-4 animate-spin" />
              <h2 className="text-xl font-bold text-white mb-2">Processing Payment</h2>
              <p className="text-blue-200 mb-4">Please wait while we confirm your payment...</p>
              <div className="text-blue-300 text-sm">
                Check {pollCount + 1} of {maxPolls}
              </div>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="mx-auto w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                <CheckCircle className="h-12 w-12 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" data-testid="success-title">Payment Successful!</h2>
              <p className="text-blue-200 mb-6">{result?.message || 'Your credits have been added.'}</p>
              
              {result?.credits_added && (
                <Alert className="bg-green-500/20 border-green-500/50 text-green-100 mb-6">
                  <AlertDescription className="text-center">
                    <span className="text-2xl font-bold">+{result.credits_added}</span> credits added to your account
                    {result.new_balance && (
                      <div className="text-sm mt-1">New balance: {result.new_balance} credits</div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3">
                <Link to="/" className="block">
                  <Button className="w-full bg-green-600 hover:bg-green-700" data-testid="go-to-dashboard">
                    <ArrowRight className="mr-2 h-4 w-4" />
                    Go to Dashboard
                  </Button>
                </Link>
                <Link to="/buy-credits" className="block">
                  <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                    Buy More Credits
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Expired */}
          {status === 'expired' && (
            <>
              <XCircle className="mx-auto h-16 w-16 text-yellow-400 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Session Expired</h2>
              <p className="text-blue-200 mb-6">Your payment session has expired. Please try again.</p>
              <Link to="/buy-credits">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  Try Again
                </Button>
              </Link>
            </>
          )}

          {/* Timeout */}
          {status === 'timeout' && (
            <>
              <RefreshCw className="mx-auto h-16 w-16 text-yellow-400 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Taking Longer Than Expected</h2>
              <p className="text-blue-200 mb-6">
                Your payment may still be processing. Please check your email for confirmation or contact support.
              </p>
              <div className="space-y-3">
                <Button onClick={() => { setPollCount(0); setStatus('checking'); checkPaymentStatus(); }} className="w-full bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check Again
                </Button>
                <Link to="/" className="block">
                  <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}

          {/* Error */}
          {(status === 'error' || status === 'not_found') && (
            <>
              <XCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
              <h2 className="text-xl font-bold text-white mb-2">Something Went Wrong</h2>
              <p className="text-blue-200 mb-6">
                We couldn't verify your payment. Please check your email for confirmation or contact support.
              </p>
              <div className="space-y-3">
                <Link to="/buy-credits">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Try Again
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full bg-white/10 border-white/20 text-white hover:bg-white/20">
                    Go to Dashboard
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
